import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import styles from '@features/cms/components/inspirations/InspirationArticlesBlock/InspirationArticlesBlock.styles';
import SingleArticleInspirations from '@components/SingleArticleInspirations/SingleArticleInspirations';
import { ICmsDocument, IListingInspirationsBlock } from '@features/cms/typescript';
import { useResponsive } from '@features/responsive';
import getArticles from '@utils/pages/getArticles';
import getArticlesWithTags from '@utils/pages/getArticlesWithTags';
import Button from '@components/Button/Button';
import theme from '@styles/theme';
import { IHitsInsights } from '@components/Search/typescript';
import { TAlgoliaIdeaHit, TAlgoliaProjectHit } from '@features/categories';
type PropsType = {
  articles: ICmsDocument[];
  offset?: number;
  numberOfIdeas?: number;
  block?: IListingInspirationsBlock;
  isInterior: boolean;
  seeMoreButton?: boolean;
  defaultBackground?: string;
  hitsInsights?: IHitsInsights<TAlgoliaIdeaHit | TAlgoliaProjectHit>;
};
const InspirationArticlesBlock = ({
  articles,
  offset = 0,
  numberOfIdeas = 8,
  block = undefined,
  isInterior,
  seeMoreButton = false,
  defaultBackground = theme.rawColors.neutralWhite,
  hitsInsights = {
    hits: [],
    sendEvent: () => null
  }
}: PropsType) => {
  const {
    currentBreakpoint
  } = useResponsive();
  const [newArticles, setNewArticles] = useState<ICmsDocument[] | null>(articles);
  const [numberOfArticles, setNumberOfArticles] = useState<number>(numberOfIdeas);
  const [useSeeMoreButton, setUseSeeMoreButton] = useState<boolean>(seeMoreButton || false);
  const {
    t
  } = useTranslation();
  const getColumnCount = () => {
    if (currentBreakpoint) {
      switch (currentBreakpoint) {
        case 'mobile':
        case 'mobilelg':
          return 1;
        case 'tablet':
          return 2;
        case 'tabletlg':
          return 3;
        default:
          return 4;
      }
    }
    return 4;
  };
  const getNewArticles = async () => {
    const newArticlesResult = await getArticles(block?.quantity || numberOfArticles, isInterior ? 'interior-designs' : 'ideas');
    if (newArticlesResult?.documents) {
      setNumberOfArticles(block?.quantity || numberOfArticles);
      setNewArticles(newArticlesResult.documents);
    }
  };
  const getNewArticlesWithTags = async (activeTagsId: number[]): Promise<ICmsDocument[] | null> => {
    const newArticlesWithTagsResult = await getArticlesWithTags(activeTagsId, isInterior ? 'interior-designs' : 'ideas');
    return newArticlesWithTagsResult?.documents || null;
  };
  const getConcatArticles = async () => {
    let newConcatArticles: ICmsDocument[] = [];
    if (block?.interiorDesign?.length) newConcatArticles = block.interiorDesign as ICmsDocument[];
    if (block?.ideas?.length) newConcatArticles = block.ideas as ICmsDocument[];
    if (block?.inspirationTags && Object.keys(block.inspirationTags).length) {
      const newArticlesWithTags = await getNewArticlesWithTags(Object.keys(block.inspirationTags).map(id => +id));
      if (newArticlesWithTags?.length) newConcatArticles = newConcatArticles.concat(newArticlesWithTags);
    }
    setNewArticles(newConcatArticles);
    setNumberOfArticles(newConcatArticles.length);
    setUseSeeMoreButton(false);
  };
  const getLink = () => isInterior ? '/inspirations/interior-designs' : '/inspirations/ideas';
  useEffect(() => {
    if (!articles.length && (block?.ideas?.length || block?.interiorDesign?.length || block?.inspirationTags && Object.keys(block.inspirationTags).length)) {
      getConcatArticles();
    } else if (!newArticles?.length && !articles.length) {
      getNewArticles();
    } else if (articles.length) {
      setNumberOfArticles(numberOfIdeas);
      setNewArticles(articles);
    }
  }, [articles.length]);
  useEffect(() => {
    if (articles.length) {
      setNumberOfArticles(numberOfIdeas);
    }
  }, [numberOfIdeas]);
  const handleOnClick = (article: ICmsDocument) => {
    if (hitsInsights.hits.length > 0) {
      const hit = hitsInsights.hits.find(hitObject => hitObject.id === article.id);
      if (hit) {
        hitsInsights.sendEvent('click', hit, 'Hit Clicked');
      }
    }
  };
  return <>
      <section id={block ? block.code : 'articles'} sx={styles.section(block ? block.background : defaultBackground, block ? block?.marginTop : 0, block ? block?.marginBottom : 0)} data-cy={block ? block.code : 'articles'}>
        <div sx={styles.wrapper} data-testid={`InspirationArticlesBlock-wrapper-${block?.code}`}>
          {newArticles && newArticles?.length > 0 && newArticles.slice(offset, offset + numberOfArticles).map((article, index) => (index + 1) % getColumnCount() === 0 ? <Fragment key={`${article.id}-${Math.random()}`}>
                  <SingleArticleInspirations article={article} isInterior={isInterior} onClick={() => handleOnClick(article)} onAuxClick={() => handleOnClick(article)} data-testid={`InspirationArticlesBlock-singleArticleInspirations-${index}`} />
                  <div sx={styles.separator} />
                </Fragment> : <SingleArticleInspirations article={article} key={`${article.id}-${Math.random()}`} isInterior={isInterior} onClick={() => handleOnClick(article)} onAuxClick={() => handleOnClick(article)} data-testid={`InspirationArticlesBlock-singleArticleInspirations-${index}`} />)}
        </div>
        {useSeeMoreButton && newArticles && newArticles?.length > 0 && <div sx={styles.containerButton}>
            <Button variant="outlined" variantColor="dark" size="small" href={getLink()} cy={`${block?.code ?? 'articles'}-seeMore`}>
              {t('seeMore')}
            </Button>
          </div>}
      </section>
    </>;
};
export default InspirationArticlesBlock;